import { useEffect, useState, useRef } from 'react';
import { scrollToElement } from '../../../utils';
import { useMedia, useTracking } from '../../../hooks';
import {
  resultCases,
  questions,
  warmwasserTipp,
} from '../HeizungsCheck.config';

const useHeizungsCheck = () => {
  const [step, setStep] = useState(1);
  const [answers, setAnswers] = useState([]);
  const [results, setResults] = useState(null);
  const [heatPotential, setHeatPotential] = useState(null);
  const localref = useRef();
  const isMobile = useMedia('(max-width: 767px)');
  const isFinalStep = step === questions.length + 1;
  const track = useTracking();

  const calculateResult = () => {
    const finalPoints =
      100 - answers.reduce((acc, { points }) => acc + points, 0);

    const [resultCase] = resultCases.filter(
      ({ min, max }) => finalPoints >= min && finalPoints <= max,
    );

    return { finalPoints, ...resultCase };
  };

  const getHeatPotential = () => {
    const heatPotential = questions?.map(({ tipps, title, id }) => {
      // filter tipps based on condition
      const filteredTipps = tipps?.filter(tipp => {
        if (tipp.condition(answers)) {
          return tipp;
        }
      });

      if (tipps && filteredTipps.length > 0) {
        return {
          id,
          question: title,
          answer: answers[id - 1].text,
          tipps: filteredTipps,
        };
      }
    });
    const finalHeatPotential = heatPotential.filter(Boolean);

    // warmwasserTipp is always shown
    finalHeatPotential.push({
      tipps: warmwasserTipp,
    });

    setHeatPotential(finalHeatPotential);
  };

  useEffect(() => {
    if (!isFinalStep) {
      track({
        event: 'HeizungsCheck',
        steps: step,
      });
    }

    if (isFinalStep) {
      const calculatedResult = calculateResult();
      setResults(calculatedResult);

      track({
        event: 'HeizungsCheck',
        steps: 'result',
        answers: calculatedResult?.trackingColor,
      });

      getHeatPotential();
    }
    scrollToElement(localref?.current, isMobile);
  }, [step]);

  const [CurrentStepComponent] = questions.filter(({ id }) => id === step);

  const handleNextClick = (text, param, label, points) => {
    // if next question has a valid condition, skip it and set param to 0
    if (!!questions[step]?.condition && questions[step]?.condition(answers)) {
      const updatedAnswers = [
        ...answers,
        { id: CurrentStepComponent.id, text, param, label, points },
        {
          id: questions[step].id,
          text: null,
          param: 0,
          label: questions[step].label,
          points: 0,
        },
      ];
      setAnswers(updatedAnswers);
      setStep(prev => prev + 2);
    } else {
      // go to next step
      const updatedAnswers = [
        ...answers,
        { id: CurrentStepComponent.id, text, param, label, points },
      ];
      setAnswers(updatedAnswers);
      setStep(prev => prev + 1);
    }
  };

  const handleBackClick = () => {
    // if previous question has a valid condition, remove last two answers
    if (
      !!questions[step - 2]?.condition &&
      questions[step - 2]?.condition(answers)
    ) {
      const updatedAnswers = answers?.toSpliced(answers?.length - 2, 2);
      setAnswers(updatedAnswers);
      setStep(prev => prev - 2);
    } else {
      // go to previous step, and remove last answer
      const updatedAnswers = answers?.toSpliced(answers?.length - 1, 1);
      setAnswers(updatedAnswers);
      setStep(prev => prev - 1);
    }
  };

  return {
    CurrentStepComponent,
    handleBackClick,
    handleNextClick,
    isFinalStep,
    results,
    step,
    heatPotential,
    localref,
  };
};

export { useHeizungsCheck };
